import type React from 'react';
import { Info } from 'lucide-react';
import type { PollRead } from '@models/api/poll';
import { usePostSheetCloseFromBody } from '@components/PostSheet/postSheetCloseContext';
import { POLL_COPY } from './pollCopy';
import {
  openPollSignIn,
  savePollPicks,
  usePollSignInPrompt,
  useSignedOutPicks,
} from './pollSignIn';

/** The place B7 shows the avatar of: the poll's home Space (else its Event). */
export interface PollSignInPlace {
  name: string;
  imageUrl: string | null;
}

export interface PollSignedOutAnswer {
  /** The guest's picks on this poll (options it still has), held as the `VOTE_POLL` intent. */
  picks: string[];
  /** Its sign-in drawer was closed without signing in, in this tab (E5). */
  dismissed: boolean;
  onToggle: (optionId: string) => void;
  /** Opens B7 with the picks (B7b's button, E5's link). */
  openSignIn: () => void;
  /** B7b's button and line: ticking a "Pick one or more" poll, its drawer not yet turned down. */
  showSignInBlock: boolean;
  /**
   * E5's line: a pick held while its drawer is closed, once turned down (or a "Pick one" pick,
   * which only a tap that opened the drawer makes; a reload forgets the turning down, not the pick).
   */
  showSavedLine: boolean;
}

/**
 * Answering signed out (TECH-197 § Voting "Signed out"; B7, B7b, E5, OPTION 1 · 1c): nothing is
 * asked up front. "Pick one": the tap ticks, the Post sheet closes (when the card is in one) and
 * today's sign-in drawer opens with the pick (B7). "Pick one or more": the options tick freely and
 * "Sign in to submit your answers" opens the same drawer. The picks are the `VOTE_POLL` intent, so
 * they stay ticked in this tab while it lives, and are cast after sign-in on any device
 * (`executePostAuthIntent`).
 */
export const usePollSignedOutAnswer = ({
  questionId,
  poll,
  place,
  enabled,
}: {
  questionId: string;
  poll: PollRead;
  place: PollSignInPlace | null;
  enabled: boolean;
}): PollSignedOutAnswer => {
  const { optionIds, dismissed } = useSignedOutPicks(questionId);
  const promptOpen = usePollSignInPrompt()?.questionId === questionId;
  const closeSheet = usePostSheetCloseFromBody();
  // Only ids this poll still has, in its order (an edit before sign-in can replace options).
  const inOrder = (ids: Iterable<string>) => {
    const wanted = new Set(ids);
    return poll.options.filter(option => wanted.has(option.id)).map(option => option.id);
  };
  const picks = enabled ? inOrder(optionIds) : [];

  const openFor = (ids: string[]) => {
    const texts = poll.options.filter(option => ids.includes(option.id)).map(o => o.text);
    // One drawer at a time: the sheet goes, the sign-in drawer (drawn at the root) comes.
    closeSheet?.();
    openPollSignIn({ questionId, optionTexts: texts, place });
  };

  const onToggle = (optionId: string) => {
    if (!poll.allows_multiple) {
      savePollPicks(questionId, [optionId]);
      openFor([optionId]);
      return;
    }
    const mine = new Set(picks);
    if (mine.has(optionId)) mine.delete(optionId);
    else mine.add(optionId);
    savePollPicks(questionId, inOrder(mine));
  };

  const held = picks.length > 0 && !promptOpen;
  return {
    picks,
    dismissed,
    showSignInBlock: held && poll.allows_multiple && !dismissed,
    showSavedLine: held && (dismissed || !poll.allows_multiple),
    onToggle,
    openSignIn: () => {
      openFor(picks);
    },
  };
};

/**
 * B7b, inside the card between the options and the footer, while a guest ticks a "Pick one or
 * more" poll (P8): the teal "Sign in to submit your answers" and the line under it.
 */
export const PollSignInBlock: React.FC<{ onSignIn: () => void }> = ({ onSignIn }) => (
  <div className="flex flex-col gap-1.5" data-testid="poll-sign-in-block">
    <button
      type="button"
      className="h-11 w-full rounded-full bg-mb-teal-ink text-[15px] leading-5 font-semibold text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink"
      onClick={event => {
        // The card's own click would open the Post sheet.
        event.stopPropagation();
        onSignIn();
      }}
    >
      {POLL_COPY.signInToSubmitAnswers}
    </button>
    <p className="text-center text-xs leading-4 text-mb-dark-gray">
      {POLL_COPY.tickAsManyAsYouLike}
    </p>
  </div>
);

/**
 * E5, under the card once its sign-in drawer was closed without signing in: "Your answer is saved.
 * It will count once you sign in.", "sign in" a link back to B7 (P12: the B7b button is not repeated).
 */
export const PollSavedLine: React.FC<{ onSignIn: () => void; className?: string }> = ({
  onSignIn,
  className,
}) => (
  <p
    className={`flex items-start gap-2 px-2 text-sm leading-[18px] text-mb-gray-dark ${className ?? ''}`}
    data-testid="poll-saved-line"
  >
    <Info aria-hidden="true" className="mt-px size-4 shrink-0 text-mb-gray-dark" strokeWidth={2} />
    <span>
      {POLL_COPY.savedLineBefore}
      <button
        type="button"
        className="font-semibold text-mb-teal-ink underline underline-offset-auto focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink"
        onClick={event => {
          event.stopPropagation();
          onSignIn();
        }}
      >
        {POLL_COPY.savedLineLink}
      </button>
      {POLL_COPY.savedLineAfter}
    </span>
  </p>
);
